import React, { useMemo } from 'react';
import { GameState, FreeAgent } from '../../../domain/entities';
import { PlayerFace } from '../../shared/PlayerFace';
import { View } from '../../../App';
import { soundService } from '../../../services/SoundService';

interface MyActiveBidsProps {
    gameState: GameState;
    pushView: (view: View) => void;
}

export const MyActiveBids: React.FC<MyActiveBidsProps> = ({
    gameState,
    pushView,
}) => {
    const activeBids = useMemo(() => {
        return (gameState.freeAgents || []).filter(fa => {
            // Defensive check: ensure required properties exist
            if (!fa.bidDeadline || !fa.playerName || !fa.playerId) {
                return false;
            }
            return fa.userBidStatus === 'submitted' || fa.userBidStatus === 'accepted';
        });
    }, [gameState.freeAgents]);

    const getBidStatusLabel = (status: string | undefined): string => {
        switch (status) {
            case 'submitted': return 'Bid Submitted';
            case 'accepted': return 'Accepted - Deciding';
            default: return 'Unknown';
        }
    };

    const getBidStatusColor = (status: string | undefined): string => {
        switch (status) {
            case 'submitted': return 'var(--accent-color)';
            case 'accepted': return 'var(--positive-color)';
            default: return 'var(--text-color)';
        }
    };

    const isDeadlinePassed = (bidDeadline: { season: number; week: number }): boolean => {
        if (gameState.season > bidDeadline.season) return true;
        if (gameState.season === bidDeadline.season && gameState.currentWeek >= bidDeadline.week) return true;
        return false;
    };

    const getWeeksUntilDeadline = (bidDeadline: { season: number; week: number }): number => {
        if (isDeadlinePassed(bidDeadline)) return 0;

        const weeksPerSeason = gameState.config.WEEKS_PER_SEASON;
        let weeksRemaining = 0;

        if (gameState.season === bidDeadline.season) {
            weeksRemaining = bidDeadline.week - gameState.currentWeek;
        } else {
            weeksRemaining = (weeksPerSeason - gameState.currentWeek) + bidDeadline.week;
        }

        return Math.max(0, weeksRemaining);
    };

    if (activeBids.length === 0) {
        return (
            <div className="panel">
                <div className="panel-header">My Active Bids</div>
                <div className="panel-content" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    <p>You have no active bids. Bid on free agents from the Free Agents tab.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="panel">
            <div className="panel-header">My Active Bids ({activeBids.length})</div>
            <div className="table-wrapper">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Player</th>
                            <th>Bid Status</th>
                            <th>Your Bid</th>
                            <th>Deadline</th>
                            <th>Weeks Left</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {activeBids.map((bid) => {
                            // Defensive check for required properties
                            if (!bid.bidDeadline) {
                                return null;
                            }

                            const weeksLeft = getWeeksUntilDeadline(bid.bidDeadline);
                            const isExpired = isDeadlinePassed(bid.bidDeadline);

                            return (
                                <tr key={bid.playerId} style={{ opacity: isExpired ? 0.6 : 1 }}>
                                    <td style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <PlayerFace player={{ hue: bid.hue || 0, faceEmoji: bid.faceEmoji || '😊', isLegend: false } as any} />
                                        <button
                                            className="link-style"
                                            onClick={() => pushView({ type: 'playerDetail', title: 'Player Detail', playerId: bid.playerId })}
                                        >
                                            {bid.playerName}
                                        </button>
                                    </td>
                                    <td style={{ color: getBidStatusColor(bid.userBidStatus), fontWeight: '600' }}>
                                        {getBidStatusLabel(bid.userBidStatus)}
                                    </td>
                                    <td>
                                        {bid.userBid && bid.userBid.wage ? (
                                            <span>£{bid.userBid.wage.toLocaleString()}/wk</span>
                                        ) : (
                                            <span style={{ opacity: 0.5 }}>—</span>
                                        )}
                                    </td>
                                    <td>S{bid.bidDeadline.season}W{bid.bidDeadline.week}</td>
                                    <td style={{ textAlign: 'center', fontWeight: weeksLeft <= 1 ? 'bold' : 'normal', color: weeksLeft <= 1 ? 'var(--warning-color)' : 'var(--text-color)' }}>
                                        {isExpired ? 'Expired' : `${weeksLeft}w`}
                                    </td>
                                    <td>
                                        <button
                                            className="button small"
                                            onClick={() => {
                                                soundService.playClick();
                                                pushView({ type: 'playerDetail', title: 'Player Detail', playerId: bid.playerId });
                                            }}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
